import { useEffect, useRef } from "react";
import { useAuth } from "./use-auth";

function getOrCreateSessionId(): string {
  const key = "fm_session_id";
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
  }
  return id;
}

/**
 * Logs a search query to the native analytics system.
 * Debounced — fires 1.5 seconds after the query stops changing.
 * Skipped for authenticated (admin/staff) users.
 * @param query      The current search string
 * @param resultsCount  How many results the search returned
 */
export function useSearchQuery(query: string, resultsCount: number) {
  const { isAuthenticated } = useAuth();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSent = useRef<string>("");

  useEffect(() => {
    if (isAuthenticated) return;
    if (!query || query.trim().length < 2) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      const trimmed = query.trim();
      if (trimmed === lastSent.current) return;
      lastSent.current = trimmed;

      const sessionId = getOrCreateSessionId();
      fetch("/api/analytics/search-query", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: trimmed, resultsCount, sessionId }),
        keepalive: true,
      }).catch(() => {});
    }, 1500);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, resultsCount, isAuthenticated]);
}
